import { createBrowserRouter } from 'react-router-dom';

// shared pages
import { ChannelsLayout, channelsRoutes } from '@pages/channels';
import { inventoryRoutes, InventoryLayout } from '@pages/inventory';
import { locationsRoutes, LocationsLayout } from '@pages/locations';
import NotFound from '@pages/errors/NotFound';
import { ordersRoutes, OrdersLayout } from '@pages/orders';
import { ReportsLayout, reportsRoutes } from '@pages/reports';
import { settingsRoutes } from '@pages/settings';
import { SubscriptionRouter } from '@pages/subscription';
import { upgradeRoutes, UpgradeLayout, plansLoader } from '@pages/upgrade';
import { Login } from '@pages/login';
import { Logout } from '@pages/logout';
import { Home } from '@pages/home';
import { Impersonate } from '@pages/impersonate';
import { RootLayout } from '@pages/rootLayout';
import NoShops from '@pages/errors/NoShops';

// product-specific pages
import { insightsRoutes, InsightsLayout } from './pages/insights';
import { DashboardOverview } from './pages/dashboard-overview';
import { RegisterLayout } from './pages/register';
import { OnboardingLayout, OnboardingFallback } from './pages/onboarding';

import { RedirectWithShopId } from '@utils/RedirectWithShopId';

const redirectToUnifyDashboard = (path) => {
	const host = window.location.hostname;
	const base = host.includes('staging')
		? 'https://staging.app.unify.near.st'
		: host.includes('preview')
			? 'https://preview.app.unify.near.st'
			: 'https://app.unify.near.st';
	window.location.replace(`${base}${path}`);
	return null;
};

export const router = createBrowserRouter(
	[
		{ path: '/login', element: <Login /> },
		{ path: '/unify-onboarding', lazy: () => ({ Component: () => redirectToUnifyDashboard('/onboarding') }) },
		{ path: '/impersonate', element: <Impersonate /> },
		{ path: '/register', element: <RegisterLayout /> },
		{
			path: '/',
			element: <RootLayout />,
			children: [
				// both chain & singles
				{ index: true, element: <Home /> },
				{ path: 'home/:shopId?', element: <Home /> },
				{ path: 'dashboard/:shopId?', element: <DashboardOverview /> },
				{ path: 'orders/:shopId?', element: <OrdersLayout />, children: ordersRoutes },
				{ path: 'insights', element: <InsightsLayout />, children: insightsRoutes },
				{ path: 'inventory', element: <InventoryLayout />, children: inventoryRoutes },
				{ path: 'settings', children: settingsRoutes },
				{ path: 'reports/:shopId?', element: <ReportsLayout />, children: reportsRoutes },
				{ path: 'logout/:shopId?', element: <Logout /> },
				{
					path: 'upgrade/:productId/:period',
					element: <UpgradeLayout />,
					children: upgradeRoutes,
					id: 'upgrade',
					loader: plansLoader
				},

				// singles only
				{ path: 'channels/:shopId?', element: <ChannelsLayout />, children: channelsRoutes },
				{ path: 'subscription/:shopId?', element: <SubscriptionRouter /> },
				{ path: 'onboarding/:shopId', element: <OnboardingLayout /> },

				// chain only
				{ path: 'locations', element: <LocationsLayout />, children: locationsRoutes },

				// chain fallbacks & redirects
				{ path: 'onboarding', element: <OnboardingFallback /> },

				// singles fallbacks & redirects
				{ path: 'locations', element: <RedirectWithShopId to="/home:shopId" /> },

				// handle empty org
				{ path: 'no-shops', element: <NoShops /> },

				// fallback
				{
					path: '*',
					element: <NotFound />
				}
			]
		},
		// fallback
		{ path: '*', element: <NotFound /> }
	],
	{
		//  future flags we should opt into now to make our eventual v7 upgrade smoother
		future: {
			v7_skipActionStatusRevalidation: true,
			v7_fetcherPersist: true,
			v7_startTransition: true,
			// v7_relativeSplatPath: true,
			v7_normalizeFormMethod: true,
			v7_partialHydration: true
		}
	}
);
